import $ from 'jquery';
import MI from './math';
import applyBrandTheme from './theme';

/*
 * Issue #296: the Calculate Mortgage Insurance popup.
 *
 * Bootstrap 4 modal, same pattern as save-results/modal.js: injected into <body> on demand (the calculator
 * UI is built client-side, so there is no server-rendered place for it), Bootstrap supplies the focus trap
 * and Escape / backdrop / X dismissal, and focus is handed back to the opening button on hidden.bs.modal.
 *
 * Two bodies share one shell:
 *   purchase    -- Home Price input + a live 5/10/15/20% tier table (<=640px: stacked cards)
 *   refinance   -- Current Home Value input + one LTV-driven estimate
 *
 * The modal never touches the calculator. Choosing "Use" hands {annual, monthly, caption} to opts.onUse and
 * closes; the caller owns write-back. Closing any other way calls nothing but opts.onClosed(false).
 */

const MODAL_ID = 'cmg-mi-estimator-modal';

const DISCLAIMER = 'Estimates for conventional loans only, based on estimated rate factors. Your actual mortgage insurance premium is set by the insurer at application and may differ. This is not a loan offer or a quote.';

const DASH = '—';

function money(n) {
    return '$' + Math.round(n).toLocaleString('en-US');
}

function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function chip(label, value, withNote) {
    return '<span class="mi-estimator-chip"><span class="mi-estimator-chip-label">' + escapeHtml(label) + '</span> &middot; '
        + '<b class="mi-estimator-chip-value">' + escapeHtml(value) + '</b>'
        + (withNote ? ' <small class="mi-estimator-chip-note">from your calculator</small>' : '') + '</span>';
}

// button-primary supplies the brand's primary colour (the brand sheet loads after main.css); the modal's own
// .mi-estimator-use rule only reshapes it.
function useButton(label, key, ariaSuffix) {
    return '<button type="button" class="button-cmg button-primary mi-estimator-use"'
        + ' data-mi-use="' + key + '" aria-label="' + escapeHtml(label + ' ' + ariaSuffix) + '">' + escapeHtml(label) + '</button>';
}

function template(opts) {
    const refinance = opts.kind === 'refinance';
    const fieldLabel = refinance ? 'Current Home Value' : 'Home Price';
    const fieldHelper = refinance
        ? 'Required — your home\'s estimated value today.'
        : 'Estimated from your loan amount — adjust to the purchase price.';
    const inputId = MODAL_ID + '-value';

    const chips = (refinance ? chip('New loan amount', money(opts.loanAmount), true) : '')
        + chip('Loan term', opts.termText, true) + chip('Interest rate', opts.rateText, true);

    return ''
        + '<div class="modal fade cmg-mi-estimator" id="' + MODAL_ID + '" tabindex="-1" role="dialog"'
        + '     aria-modal="true" aria-labelledby="' + MODAL_ID + '-title">'
        + '  <div class="modal-dialog modal-dialog-centered" role="document">'
        + '    <div class="modal-content">'
        + '      <span class="mi-estimator-grabber" aria-hidden="true"></span>'
        + '      <div class="modal-header">'
        + '        <h4 class="modal-title" id="' + MODAL_ID + '-title">Estimate Your Mortgage Insurance</h4>'
        + '        <button type="button" class="close" data-dismiss="modal" aria-label="Close">&times;</button>'
        + '      </div>'
        + '      <div class="modal-body">'
        + '        <div class="mi-estimator-chips">' + chips + '</div>'
        + '        <div class="mi-estimator-field">'
        + '          <label for="' + inputId + '">' + fieldLabel + '</label>'
        + '          <div class="mi-estimator-input"><i>$</i>'
        + '            <input type="text" id="' + inputId + '" inputmode="numeric" autocomplete="off" data-mi-value />'
        + '          </div>'
        + '          <p class="mi-estimator-field-helper">' + escapeHtml(fieldHelper) + '</p>'
        + '        </div>'
        + '        <div class="mi-estimator-results" aria-live="polite" data-mi-results></div>'
        + '        <p class="mi-estimator-disclaimer">' + DISCLAIMER + '</p>'
        + '        <div class="mi-estimator-actions">'
        + '          <button type="button" class="mi-estimator-cancel" data-dismiss="modal">Cancel</button>'
        + '        </div>'
        + '      </div>'
        + '    </div>'
        + '  </div>'
        + '</div>';
}

function cardHtml(modifier, pctHtml, badgeHtml, rowsHtml, actionHtml) {
    return '<div class="mi-estimator-card' + modifier + '">'
        + '<div class="mi-estimator-card-info">'
        + '<div class="mi-estimator-card-head"><b class="mi-estimator-card-pct">' + pctHtml + '</b>' + badgeHtml + '</div>'
        + '<div class="mi-estimator-card-rows">' + rowsHtml + '</div>'
        + '</div>' + actionHtml + '</div>';
}

function purchaseResultsHtml(tierList, closestPercent) {
    const valid = !!tierList;
    const list = tierList || MI.TIER_PERCENTS.map((percent) => ({ percent }));

    const rows = list.map((t) => {
        const notRequired = valid && t.status === 'notRequired';
        const unavailable = !valid || t.status === 'none';
        const closest = valid && t.percent === closestPercent;
        const label = notRequired ? 'Use $0' : 'Use';
        const suffix = 'with ' + t.percent + '% down';

        const miCells = notRequired
            ? '<td colspan="2" class="mi-estimator-notrequired">NOT REQUIRED</td>'
            : '<td>' + (unavailable ? DASH : money(t.monthly)) + '</td><td>' + (unavailable ? DASH : money(t.annual)) + '</td>';

        return '<tr class="mi-estimator-row' + (closest ? ' mi-estimator-row--closest' : '') + '">'
            + '<td class="mi-estimator-tier">' + t.percent + '%</td>'
            + '<td>' + (valid ? money(t.downPayment) : DASH) + '</td>'
            + '<td>' + (valid ? money(t.impliedLoan) : DASH) + '</td>'
            + miCells
            + '<td class="mi-estimator-action">' + (valid && !unavailable ? useButton(label, t.percent, suffix) : '') + '</td>'
            + '</tr>';
    }).join('');

    const cards = list.map((t) => {
        const notRequired = valid && t.status === 'notRequired';
        const unavailable = !valid || t.status === 'none';
        const closest = valid && t.percent === closestPercent;
        const label = notRequired ? 'Use $0' : 'Use';
        const suffix = 'with ' + t.percent + '% down';

        const miRow = notRequired
            ? '<b class="mi-estimator-card-mi">NOT REQUIRED</b>'
            : '<b class="mi-estimator-card-mi">' + (unavailable ? DASH : money(t.monthly)) + '/mo</b> <span class="mi-estimator-card-label">&middot;</span> '
              + (unavailable ? DASH : money(t.annual)) + '/yr';

        return cardHtml(closest ? ' mi-estimator-card--closest' : '',
            t.percent + '% down &middot; ' + (valid ? money(t.downPayment) : DASH),
            closest ? '<span class="mi-estimator-badge">Closest match</span>' : '',
            '<span class="mi-estimator-card-label">Loan amount</span> ' + (valid ? money(t.impliedLoan) : DASH) + '<br>' + miRow,
            valid && !unavailable ? useButton(label, t.percent, suffix) : '');
    }).join('');

    return '<div class="mi-estimator-tablewrap"><table class="mi-estimator-table">'
        + '<thead><tr><th>Down</th><th>Down $</th><th>Loan amount</th><th>MI / mo</th><th>MI / yr</th><th><span class="sr-only">Action</span></th></tr></thead>'
        + '<tbody>' + rows + '</tbody></table></div>'
        + '<div class="mi-estimator-cards">' + cards + '</div>';
}

function refinanceResultsHtml(est, loanAmount) {
    if (est.status === 'none' && est.ltvBps === null) {
        return '<p class="mi-estimator-hint">Enter your home&rsquo;s current value to see an estimate.</p>';
    }

    const ltvText = (est.ltvBps / 100).toFixed(2) + '%';
    const suffix = 'at ' + MI.captionLtvPercent(est.ltvBps) + '% LTV';
    const label = est.status === 'notRequired' ? 'Use $0' : 'Use';
    const showUse = est.status !== 'none';

    let cells;
    let miRow;
    if (est.status === 'none') {
        cells = '<td colspan="3" class="mi-estimator-message">' + escapeHtml(MI.ABOVE_MAX_LTV_MESSAGE) + '</td>';
        miRow = '<span class="mi-estimator-message">' + escapeHtml(MI.ABOVE_MAX_LTV_MESSAGE) + '</span>';
    } else if (est.status === 'notRequired') {
        cells = '<td colspan="2" class="mi-estimator-notrequired">NOT REQUIRED</td><td class="mi-estimator-action">' + useButton(label, 'ref', suffix) + '</td>';
        miRow = '<b class="mi-estimator-card-mi">NOT REQUIRED</b>';
    } else {
        cells = '<td>' + money(est.monthly) + '</td><td>' + money(est.annual) + '</td><td class="mi-estimator-action">' + useButton(label, 'ref', suffix) + '</td>';
        miRow = '<b class="mi-estimator-card-mi">' + money(est.monthly) + '/mo</b> <span class="mi-estimator-card-label">&middot;</span> ' + money(est.annual) + '/yr';
    }

    return '<div class="mi-estimator-tablewrap"><table class="mi-estimator-table">'
        + '<thead><tr><th>LTV</th><th>MI / mo</th><th>MI / yr</th><th><span class="sr-only">Action</span></th></tr></thead>'
        + '<tbody><tr class="mi-estimator-row"><td class="mi-estimator-tier">' + ltvText + '</td>' + cells + '</tr></tbody></table></div>'
        + '<div class="mi-estimator-cards">'
        + cardHtml(' mi-estimator-card--single', 'LTV ' + ltvText, '',
            '<span class="mi-estimator-card-label">New loan amount</span> ' + money(loanAmount) + '<br>' + miRow,
            showUse ? useButton(label, 'ref', suffix) : '')
        + '</div>';
}

export default class MiEstimatorModal {
    /**
     * @param {object} opts
     *   kind          'purchase' | 'refinance'
     *   bands         normalized bands (math.normalizeBands)
     *   termText      read-only chip text, e.g. "30 yrs"
     *   rateText      read-only chip text, e.g. "7.000%"
     *   loanAmount    the visitor's loan (purchase: closest-match target; refinance: the new loan)
     *   homePrice     purchase pre-fill (number) or null
     *   returnFocusTo element to refocus on close
     *   onUse(result) {annual, monthly, caption}
     *   onClosed(used)
     */
    open(opts) {
        this.close();

        const $modal = $(template(opts)).appendTo('body');
        applyBrandTheme($modal[0]);
        const $value = $modal.find('[data-mi-value]');
        const $results = $modal.find('[data-mi-results]');
        const refinance = opts.kind === 'refinance';
        let used = false;
        let current = {};

        const render = () => {
            const value = MI.parseNumber($value.val());

            if (refinance) {
                const est = MI.estimate(opts.loanAmount, value, opts.bands);
                current = { ref: est };
                $results.html(refinanceResultsHtml(est, opts.loanAmount));
                return;
            }

            const tierList = MI.tiers(value, opts.bands);
            current = {};
            (tierList || []).forEach((t) => { current[t.percent] = t; });
            $results.html(purchaseResultsHtml(tierList, MI.closestTierPercent(opts.loanAmount, tierList)));
        };

        if (!refinance && opts.homePrice > 0) {
            $value.val(Math.round(opts.homePrice).toLocaleString('en-US'));
        }
        render();

        $value.on('input', render);
        $value.on('blur', () => {
            const n = MI.parseNumber($value.val());
            if (n > 0) $value.val(Math.round(n).toLocaleString('en-US'));
        });

        $modal.on('click', '[data-mi-use]', (e) => {
            const key = $(e.currentTarget).attr('data-mi-use');
            const picked = current[key];
            if (!picked) return;

            let result;
            if (refinance) {
                result = {
                    annual: picked.annual,
                    monthly: picked.monthly,
                    caption: MI.captionFor('refinance', { ltvBps: picked.ltvBps, notRequired: picked.status === 'notRequired' })
                };
            } else {
                result = {
                    annual: picked.annual,
                    monthly: picked.monthly,
                    caption: MI.captionFor('tier', { percent: picked.percent })
                };
            }

            used = true;
            if (opts.onUse) opts.onUse(result);
            $modal.modal('hide');
        });

        // Bootstrap 4 focuses the dialog element itself at the end of its fade, discarding focus set in
        // open() -- so the field focus rides the shown event (same as save-results/modal.js).
        $modal.on('shown.bs.modal', () => {
            $value.trigger('focus');
        });

        $modal.on('hidden.bs.modal', () => {
            const back = typeof opts.returnFocusTo === 'function' ? opts.returnFocusTo() : opts.returnFocusTo;
            if (back) $(back).trigger('focus');
            $modal.remove();
            this.$modal = null;
            if (opts.onClosed) opts.onClosed(used);
        });

        this.$modal = $modal;
        $modal.modal('show');
        // Bootstrap appends the backdrop to <body> as a sibling of the modal; tag it so the design's overlay
        // tint applies to this dialog only.
        $('.modal-backdrop').last().addClass('cmg-mi-estimator-backdrop');
    }

    /** Drops a stale instance (not reachable in normal use: the backdrop covers the opening button). */
    close() {
        if (!this.$modal) return;
        const $old = this.$modal;
        this.$modal = null;
        $old.off('hidden.bs.modal');
        $old.modal('dispose');
        $old.remove();
    }
}
